/**
 * Grouped Questions Component
 *
 * Handles sidebar expansion and scroll-spy functionality for grouped questions.
 * Similar to secondary-nav.js but with additional expand/collapse functionality.
 *
 * @component GroupedQuestions
 * @extends HTMLElement
 */
function doGroupedQuestions() {
  if (customElements.get('grouped-questions')) return

  class GroupedQuestions extends HTMLElement {
    constructor() {
      super()
      this.sections = []
      this.navItems = []
      this.isExpanded = false
      this.inner = this.querySelector('.grouped-questions')
      this.offset = 200 // default offset
      this.lastScrollY = window.scrollY // for scroll direction
      this.init()
    }

    init() {
      // Get the trigger button and nav list from the aside element
      this.trigger = this.querySelector('aside .jump-to-trigger')
      this.navList = this.querySelector('aside ul')

      // Get all nav items and their corresponding sections
      this.navItems = Array.from(this.querySelectorAll('aside li'))

      this.sections = this.navItems
        .map((item) => {
          const link = item.querySelector('a')
          const href = link.getAttribute('href')
          if (href.startsWith('#')) {
            const sectionId = href.substring(1)
            return document.getElementById(sectionId)
          }
          return null
        })
        .filter(Boolean)

      // Add event listeners
      this.trigger.addEventListener('click', () => this.onClick())
      window.addEventListener('scroll', () => this.onScrollByTop(), {
        passive: true,
      })

      // Add nav item click listeners
      this.addNavItemClickListeners()

      // Initial check
      this.onScrollByTop()
    }

    addNavItemClickListeners() {
      this.navItems.forEach((item, index) => {
        const link = item.querySelector('a')
        const href = link.getAttribute('href')

        if (href && href.startsWith('#')) {
          link.addEventListener('click', (e) => {
            e.preventDefault()

            const targetId = href.substring(1)
            const targetElement = document.getElementById(targetId)

            if (targetElement) {
              const baseOffset = 100
              const additionalOffset = 200
              const isScrollingUp = window.scrollY < this.lastScrollY
              let finalOffset =
                baseOffset + (isScrollingUp ? additionalOffset : 0)
              // Always add further 500px if first item is clicked
              if (index === 0) {
                finalOffset += 100
              }

              const elementTop =
                targetElement.getBoundingClientRect().top + window.scrollY
              const scrollTo = elementTop - finalOffset

              window.scrollTo({
                top: scrollTo,
                behavior: 'smooth',
              })

              this.onClick()

            }
          })
        }
      })
    }

    onClick = () => {
      if (window.innerWidth < 1024) {
        this.isExpanded = !this.isExpanded
        this.navList.style.maxHeight = this.isExpanded
        ? `${this.navList.scrollHeight}px`
        : '0'
        // Add styling for aside ul when max-height is 0
        if (this.navList.style.maxHeight === '0px') {
          this.navList.style.visibility = 'hidden';
        } else {
          this.navList.style.visibility = 'unset';
          this.navList.style.height = 'auto';
        }
      this.inner.classList.toggle('is-expanded', this.isExpanded)
      }
    }

    onScroll = () => {
      const viewportHeight = window.innerHeight
      let mostVisibleSection = null
      let maxVisibleArea = 0

      // Reset all active states
      this.navItems.forEach((item) => item.classList.remove('is-active'))

      // Check each section's visibility and calculate visible area
      this.sections.forEach((section, index) => {
        if (!section) return

        const rect = section.getBoundingClientRect()
        const navItem = this.navItems[index]

        // Calculate visible area
        const visibleHeight =
          Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
        const visibleArea = visibleHeight * rect.width

        // Update most visible section if this one has more visible area
        if (visibleArea > maxVisibleArea) {
          maxVisibleArea = visibleArea
          mostVisibleSection = index
        }
      })

      // Activate only the nav item for the most visible section
      if (mostVisibleSection !== null) {
        this.navItems[mostVisibleSection].classList.add('is-active')
      }
    }

    onScrollByTop = () => {
      const scrollPosition = window.scrollY
      let offset = this.offset
      // Detect scroll direction
      if (scrollPosition < this.lastScrollY) {
        // Scrolling up
        offset += 60
      }
      this.lastScrollY = scrollPosition
      let activeIndex = null

      this.sections.forEach((section, index) => {
        if (!section) return
        const sectionTop = section.getBoundingClientRect().top
        // Check if the section's top is within offset px from the top of the viewport
        if (sectionTop <= offset) {
          activeIndex = index
        }
      })

      // Reset all active states
      this.navItems.forEach((item, idx) => {
        if (idx === activeIndex) {
          item.classList.add('is-active')
        } else {
          item.classList.remove('is-active')
        }
      })
    }

    disconnectedCallback() {
      this.trigger.removeEventListener('click', this.onClick)
      window.removeEventListener('scroll', this.onScroll)
    }
  }

  customElements.define('grouped-questions', GroupedQuestions)
}

doGroupedQuestions()

document.addEventListener('DOMContentLoaded', function () {
  const aside = document.querySelector('.grouped-questions aside');
  const parent = document.querySelector('.grouped-questions .mq-container');
  if (!aside || !parent) return;

  const stopOffset = 20; // px from bottom of parent to stop fixing
  const fixedTop = 75; // px margin from top when fixed
  let asideInitialTop = null;
  let enabled = false;

  function getOffsetTop(elem) {
    let offsetTop = 0;
    while (elem) {
      offsetTop += elem.offsetTop;
      elem = elem.offsetParent;
    }
    return offsetTop;
  }

  function resetAside() {
    aside.classList.remove('is-fixed');
    aside.style.position = '';
    aside.style.top = '';
    aside.style.left = '';
    aside.style.width = '';
  }

  function onScroll() {
    if (!enabled) return;
    if (asideInitialTop === null) {
      asideInitialTop = getOffsetTop(aside);
    }
    const scrollY = window.scrollY || window.pageYOffset;
    const parentRect = parent.getBoundingClientRect();
    const parentTop = getOffsetTop(parent);
    const parentHeight = parent.offsetHeight;
    const asideHeight = aside.offsetHeight;
    const asideRect = aside.getBoundingClientRect();
    const left = asideRect.left;
    const width = asideRect.width;

    // At the very top: reset
    if (scrollY + fixedTop <= asideInitialTop) {
      resetAside();
      return;
    }

    // At the very bottom: absolute at bottom of parent
    if (scrollY + asideHeight + fixedTop + stopOffset >= parentTop + parentHeight - 1) {
      aside.classList.remove('is-fixed');
      aside.style.position = 'absolute';
      aside.style.top = (parentHeight - asideHeight - stopOffset) + 'px';
      aside.style.left = '0px'; // align to parent
      aside.style.width = width + 'px';
      return;
    }

    // In between: fixed with margin
    aside.classList.add('is-fixed');
    aside.style.position = 'fixed';
    aside.style.top = fixedTop + 'px';
    aside.style.left = left + 'px';
    aside.style.width = width + 'px';
  }

  function onResize() {
    if (window.innerWidth >= 1024) {
      enabled = true;
      asideInitialTop = null;
      onScroll();
      window.addEventListener('scroll', onScroll);
    } else {
      enabled = false;
      resetAside();
      window.removeEventListener('scroll', onScroll);
    }
  }

  window.addEventListener('resize', onResize);
  onResize();
});
